import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

interface BrandColors {
  primary: string;
  secondary: string;
}

export function useBrandColors() {
  const [colors, setColors] = useState<BrandColors | null>(null);
  const [loading, setLoading] = useState(true);

  const loadBrandColors = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('default_primary_color, default_secondary_color')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data && (data.default_primary_color || data.default_secondary_color)) {
        setColors({
          primary: data.default_primary_color || '#0c0f24',
          secondary: data.default_secondary_color || '#1b489b'
        });
      }
    } catch (error) {
      logger.error('Error loading brand colors:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrandColors();
  }, []);

  return { colors, loading };
}

// Helper function to convert hex to HSL
function hexToHSL(hex: string): string {
  // Remove the # if present
  hex = hex.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return `${h} ${s}% ${l}%`;
}

// Apply brand colors to the root element
export function applyBrandColors(colors: BrandColors | null) {
  if (!colors) return;

  const root = document.documentElement;
  const primaryHSL = hexToHSL(colors.primary);
  const secondaryHSL = hexToHSL(colors.secondary);

  // Apply custom brand colors
  root.style.setProperty('--primary', primaryHSL);
  root.style.setProperty('--secondary', secondaryHSL);
  root.style.setProperty('--sidebar-primary', primaryHSL);
  root.style.setProperty('--ring', primaryHSL);
  root.style.setProperty('--sidebar-ring', primaryHSL);
}

// Remove brand colors (restore defaults)
export function removeBrandColors() {
  const root = document.documentElement;
  root.style.removeProperty('--primary');
  root.style.removeProperty('--secondary');
  root.style.removeProperty('--sidebar-primary');
  root.style.removeProperty('--ring');
  root.style.removeProperty('--sidebar-ring');
}
